import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Activity, ArrowRight, Building2, CheckCircle2, CircleSlash2, ExternalLink,
  ListChecks, LogOut, Plus, RefreshCw, ShieldCheck, UsersRound, X,
} from 'lucide-react';
import { api, get, patch, post, setWorkspaceSlug } from '../api';
import { BrandLogo, Button, ErrorState, Loading, useModalScrollLock } from '../components';

interface PlatformTenant {
  id: number;
  publicId: string;
  slug: string;
  name: string;
  legalName?: string | null;
  status: string;
  plan: string;
  timezone: string;
  currency: string;
  ownerName: string;
  ownerEmail: string;
  health: string;
  createdAt: string;
  lastOpenedAt?: string | null;
  provisioningError?: string | null;
  metrics?: {
    users?: number;
    employees?: number;
    clients?: number;
    projects?: number;
    openTasks?: number;
    attendanceToday?: number;
    payrollRuns?: number;
    error?: string;
  } | null;
}

interface PlatformBootstrap {
  user: { id: number; name: string; email: string; role: string };
  totals: { tenants: number; activeTenants: number; unavailableTenants: number; employees: number; users: number; openTasks: number };
  tenants: PlatformTenant[];
  activity: Array<{ id: number; action: string; summary: string; actor_name?: string | null; tenant_name?: string | null; created_at: string }>;
}

function dateTime(value?: string | null) {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

export function PlatformAdminPage() {
  const [data, setData] = useState<PlatformBootstrap | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  useModalScrollLock(createOpen);

  const load = useCallback(async () => {
    try {
      const result = await get<PlatformBootstrap>('/api/platform/bootstrap');
      setData(result); setError('');
    } catch (requestError) {
      const status = (requestError as { status?: number }).status;
      if (status === 401) setData(null);
      else setError((requestError as Error).message);
    } finally { setChecking(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function login(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy('login'); setError('');
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try { await post('/api/platform/auth/login', values); await load(); }
    catch (requestError) { setError((requestError as Error).message); }
    finally { setBusy(''); }
  }

  async function createTenant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy('create'); setError('');
    const form = event.currentTarget;
    const values = Object.fromEntries(new FormData(form));
    try {
      await post('/api/platform/tenants', values);
      setCreateOpen(false); form.reset(); await load();
    } catch (requestError) { setError((requestError as Error).message); }
    finally { setBusy(''); }
  }

  async function changeStatus(tenant: PlatformTenant, status: 'active' | 'suspended' | 'archived') {
    const message = status === 'suspended'
      ? `Suspend ${tenant.name}? Its users will immediately lose access.`
      : status === 'archived' ? `Archive ${tenant.name}? The workspace data will be preserved but unavailable.`
        : `Reactivate ${tenant.name}?`;
    if (!window.confirm(message)) return;
    setBusy(`status-${tenant.id}`); setError('');
    try { await patch(`/api/platform/tenants/${tenant.id}`, { status }); await load(); }
    catch (requestError) { setError((requestError as Error).message); }
    finally { setBusy(''); }
  }

  async function openTenant(tenant: PlatformTenant) {
    setBusy(`open-${tenant.id}`); setError('');
    try {
      await post(`/api/platform/tenants/${tenant.id}/access`);
      setWorkspaceSlug(tenant.slug);
      window.location.assign('/');
    } catch (requestError) { setError((requestError as Error).message); setBusy(''); }
  }

  async function logout() {
    await api('/api/platform/auth/logout', { method: 'POST' });
    setData(null);
  }

  if (checking) return <div className="platform-loading"><Loading label="Opening Nosivo control plane" /></div>;

  if (!data) return <main className="platform-login">
    <section className="platform-login__story">
      <BrandLogo surface="dark" />
      <div><span><ShieldCheck size={15} /> Nosivo control plane</span><h1>Every company.<br />One secure overview.</h1><p>Provision isolated workspaces, monitor company activity, and enter a tenant through an audited super-admin session.</p></div>
      <small>Platform administrators only</small>
    </section>
    <section className="platform-login__panel"><form onSubmit={login}>
      <span className="eyebrow">Super admin</span><h2>Control-plane sign in</h2><p>This login is separate from every company owner and employee account.</p>
      {error && <ErrorState message={error} />}
      <label><span>Email</span><input name="email" type="email" autoComplete="username" required /></label>
      <label><span>Password</span><input name="password" type="password" autoComplete="current-password" required /></label>
      <Button type="submit" busy={busy === 'login'}>Sign in <ArrowRight size={16} /></Button>
      <a href="/login">Return to company login</a>
    </form></section>
  </main>;

  return <main className="platform-page">
    <header className="platform-topbar"><div><BrandLogo surface="light" /><span>Control plane</span></div><div><strong>{data.user.name}</strong><small>{data.user.email}</small><button type="button" onClick={() => void logout()}><LogOut size={16} /> Sign out</button></div></header>
    <div className="platform-content">
      <section className="platform-heading"><div><span className="eyebrow">SaaS operations</span><h1>Company workspaces</h1><p>Live, isolated workspaces managed from one audited Nosivo overview.</p></div><div><Button variant="secondary" icon={RefreshCw} onClick={() => void load()}>Refresh</Button><Button icon={Plus} onClick={() => setCreateOpen(true)}>Add company</Button></div></section>
      {error && <ErrorState message={error} />}
      <section className="platform-stats">
        <article><span><Building2 /></span><div><small>All companies</small><strong>{data.totals.tenants}</strong><em>{data.totals.activeTenants} active{data.totals.unavailableTenants ? ` · ${data.totals.unavailableTenants} unavailable` : ''}</em></div></article>
        <article><span><UsersRound /></span><div><small>Company users</small><strong>{data.totals.users}</strong><em>{data.totals.employees} employees</em></div></article>
        <article><span><ListChecks /></span><div><small>Open tasks</small><strong>{data.totals.openTasks}</strong><em>Across active tenants</em></div></article>
        <article><span><ShieldCheck /></span><div><small>Isolation</small><strong>Per tenant</strong><em>Database and session boundary</em></div></article>
      </section>
      <section className="platform-table-card">
        <header><div><h2>Tenant directory</h2><p>Owners manage their own company; only the Nosivo super admin can see this directory.</p></div><span>{data.tenants.length} workspaces</span></header>
        <div className="platform-table-wrap"><table><thead><tr><th>Company</th><th>Owner</th><th>Usage</th><th>Region</th><th>Status</th><th>Last opened</th><th /></tr></thead><tbody>
          {data.tenants.map((tenant) => <tr key={tenant.id}>
            <td><div className="platform-company"><span>{tenant.name.slice(0, 2).toUpperCase()}</span><div><strong>{tenant.name}</strong><small>{tenant.slug} · {tenant.plan}</small></div></div></td>
            <td><div className="platform-owner"><strong>{tenant.ownerName}</strong><small>{tenant.ownerEmail}</small></div></td>
            <td><div className="platform-usage"><span>{tenant.metrics?.employees || 0} employees</span><span>{tenant.metrics?.projects || 0} projects</span><span>{tenant.metrics?.openTasks || 0} open tasks</span></div></td>
            <td><div className="platform-owner"><strong>{tenant.currency}</strong><small>{tenant.timezone}</small></div></td>
            <td><span className={`platform-status platform-status--${tenant.status}`}>{tenant.status === 'active' ? <CheckCircle2 /> : <CircleSlash2 />}{tenant.status}</span>{tenant.health === 'unavailable' && <small className="platform-health-error" title={tenant.metrics?.error}>Database unavailable</small>}</td>
            <td><span className="platform-date">{dateTime(tenant.lastOpenedAt)}</span></td>
            <td><div className="platform-actions"><Button disabled={tenant.status !== 'active'} busy={busy === `open-${tenant.id}`} onClick={() => void openTenant(tenant)}>Open <ExternalLink size={14} /></Button>{tenant.status === 'active' ? <button type="button" disabled={busy === `status-${tenant.id}`} onClick={() => void changeStatus(tenant, 'suspended')}>Suspend</button> : tenant.status === 'suspended' ? <button type="button" disabled={busy === `status-${tenant.id}`} onClick={() => void changeStatus(tenant, 'active')}>Activate</button> : null}</div></td>
          </tr>)}
        </tbody></table></div>
      </section>
      <section className="platform-activity"><header><Activity size={18} /><div><h2>Platform activity</h2><p>Super-admin and tenant lifecycle events.</p></div></header><div>{data.activity.slice(0, 12).map((item) => <article key={item.id}><span /><div><strong>{item.summary}</strong><small>{item.actor_name || 'System'}{item.tenant_name ? ` · ${item.tenant_name}` : ''}</small></div><time>{dateTime(item.created_at)}</time></article>)}</div></section>
    </div>
    {createOpen && <div className="platform-modal" onMouseDown={(event) => { if (event.target === event.currentTarget) setCreateOpen(false); }}><form onSubmit={createTenant}>
      <header><div><span className="eyebrow">New isolated tenant</span><h2>Add a company</h2><p>Nosivo will create a separate database, owner account, settings, and storage namespace.</p></div><button type="button" onClick={() => setCreateOpen(false)}><X /></button></header>
      <div className="platform-form-grid"><label><span>Company name</span><input name="name" required /></label><label><span>Workspace code</span><input name="slug" required pattern="[a-z0-9][a-z0-9-]{2,47}" placeholder="acme-india" /></label><label><span>Legal name</span><input name="legalName" /></label><label><span>Industry</span><input name="industry" /></label><label><span>Owner name</span><input name="ownerName" required /></label><label><span>Owner email</span><input name="ownerEmail" type="email" required /></label><label><span>Temporary owner password</span><input name="ownerPassword" type="password" minLength={10} autoComplete="new-password" required /></label><label><span>Plan</span><select name="plan" defaultValue="business"><option value="starter">Starter</option><option value="business">Business</option><option value="enterprise">Enterprise</option></select></label><label><span>Timezone</span><select name="timezone" defaultValue="Asia/Kolkata"><option>Asia/Kolkata</option><option>UTC</option><option>Europe/London</option><option>America/New_York</option></select></label><label><span>Currency</span><select name="currency" defaultValue="INR"><option>INR</option><option>USD</option><option>GBP</option><option>EUR</option></select></label><label className="platform-form-wide"><span>Registered address</span><textarea name="address" rows={2} /></label></div>
      <footer><Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button><Button type="submit" busy={busy === 'create'}>Create company workspace</Button></footer>
    </form></div>}
  </main>;
}
